import React, { useState, useEffect, useCallback } from 'react';
import Shimmer from 'sushiweb/Shimmer';
import './OrderTrackerV1.scss';
import {
  MAP_EVENTS,
  sendReloadMapEvents,
} from '../../../helpers/analytics/map.analytics';
import { RenderMap, OrderRunMap } from './organism/atoms/Map/Map';
import { ErrorMapView } from './organism/atoms/ErrorMapView';

const dashboard = window.dashboard.mode;
export const OrderTrackerV1 = ({
  orderId,
  userId,
  orderTrackInfo,
  getOrderTrackData,
  getOrderRunData,
  orderRunInfo,
  orderBasicInfo,
  showAddress,
  setShowMap,
  showMap,
  setShowAddress,
}) => {
  const isOrderLive = orderBasicInfo?.order?.is_live;
  if (isOrderLive) {
    return (
      <ETATracker
        orderId={orderId}
        userId={userId}
        orderTrackInfo={orderTrackInfo}
        getOrderTrackData={getOrderTrackData}
        orderBasicInfo={orderBasicInfo}
      />
    );
  }

  return (
    <OrderRun
      orderId={orderId}
      userId={userId}
      orderRunInfo={orderRunInfo}
      getOrderRunData={getOrderRunData}
      orderBasicInfo={orderBasicInfo}
      showAddress={showAddress}
      setShowMap={setShowMap}
      showMap={showMap}
      setShowAddress={setShowAddress}
    />
  );
};

const OrderRun = ({
  orderId,
  userId,
  orderRunInfo,
  getOrderRunData,
  orderBasicInfo,
  showAddress,
  setShowMap,
  setShowAddress,
}) => {
  const [showShimmer, setShowShimmer] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [mapError, setMapError] = useState(null);

  const makeCallToGetOrderRunData = useCallback(() => {
    let params = {
      order_id: orderId,
      user_id: userId,
    };
    return getOrderRunData(params)
      .then((res) => {
        setShowShimmer(false);
        if (res?.isError) {
          return setMapError(res);
        }
        if (res) {
          setMapError(false);
        }
      })
      .catch((err) => {
        setMapError(err);
      });
  }, [orderId, userId, getOrderRunData]);

  const refreshMap = useCallback(() => {
    setShowMap(true);
    setShowAddress(false);
    setInitialLoad(false);
    sendReloadMapEvents(MAP_EVENTS.RELOAD_MAP_EVENT, orderId, userId);
    setShowShimmer(true);
    makeCallToGetOrderRunData();
  }, [orderId, userId, makeCallToGetOrderRunData]);

  let compToRender = (
    <OrderRunMap
      orderRunInfo={orderRunInfo}
      refreshMap={refreshMap}
      initialLoad={initialLoad}
      orderBasicInfo={orderBasicInfo}
      orderId={orderId}
      userId={userId}
    />
  );

  if (mapError) {
    compToRender = (
      <ErrorMapView
        message={mapError?.message || 'Something went wrong'}
        refreshMap={refreshMap}
      />
    );
  }

  useEffect(() => {
    if (showAddress) {
      setInitialLoad(showAddress);
    }
  }, [showAddress]);

  return (
    <div
      className={`orderTrackerContainerV1 row ${
        dashboard === 'lifelineZ' ? 'tw-h-full' : 'tw-h-64'
      }`}
    >
      {showShimmer ? (
        <Shimmer
          height={dashboard === 'lifelineZ' ? 'tw-h-full' : 'tw-h-64'}
          width='100%'
        />
      ) : (
        <div className='mapContainer'>
          <div className='mapBox col'>{compToRender}</div>
        </div>
      )}
    </div>
  );
};

const ETATracker = ({
  orderId,
  userId,
  orderTrackInfo,
  getOrderTrackData,
  orderBasicInfo,
}) => {
  const [showShimmer, setShowShimmer] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const [lastUpdatedAt, setLastUpdatedAt] = useState(0);
  const [mapError, setMapError] = useState(null);

  const makeCallToGetOrderTrackData = useCallback(() => {
    let params = {
      order_id: orderId,
      user_id: userId,
    };
    return getOrderTrackData(params)
      .then((res) => {
        setShowShimmer(false);
        if (res?.isError) {
          return setMapError(res);
        }
        if (res) {
          setMapError(false);
          setLastUpdatedAt(Date.now());
        }
      })
      .catch((err) => {
        setMapError(err);
      });
  }, [orderId, userId, getOrderTrackData]);

  useEffect(() => {
    makeCallToGetOrderTrackData();
  }, []);

  const refreshMap = useCallback(() => {
    setInitialLoad(false);
    sendReloadMapEvents(MAP_EVENTS.RELOAD_MAP_EVENT, orderId, userId);
    setShowShimmer(true);
    makeCallToGetOrderTrackData();
  }, [orderId, userId, makeCallToGetOrderTrackData]);

  let compToRender = (
    <React.Fragment data-dd-privacy='hidden'>
      {orderTrackInfo?.rider_status && (
        <div
          css={`
            background-color: #256fef;
            padding: 0.5rem 0.8rem;
            color: white;
            font-weight: 500;
            font-size: 1.2rem;
            border-radius: 0.4rem 0.4rem 0px 0px;
          `}
        >
          {orderTrackInfo?.rider_status}
        </div>
      )}
      <RenderMap
        mapData={orderTrackInfo}
        orderId={orderId}
        userId={userId}
        refreshMap={refreshMap}
        lastUpdatedAt={lastUpdatedAt}
        initialLoad={initialLoad}
        orderBasicInfo={orderBasicInfo}
      />
    </React.Fragment>
  );

  if (mapError) {
    compToRender = (
      <ErrorMapView
        message={mapError?.message || 'Something went wrong'}
        refreshMap={refreshMap}
      />
    );
  }

  return (
    <div className={' orderTrackerContainerV1 row tw-h-full '}>
      {showShimmer ? (
        <Shimmer height={'tw-h-full'} width='100%' />
      ) : (
        <div className='mapContainer col'>
          <div className='mapBox col' data-dd-privacy='hidden'>
            {compToRender}
          </div>
        </div>
      )}
    </div>
  );
};
